import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calculator } from 'lucide-react';
import type { FindriscAnswers } from '../types/findrisc';

interface BmiCalculatorProps {
  answers: FindriscAnswers;
  onAnswer: (questionId: string, value: any) => void;
}

export const BmiCalculator: React.FC<BmiCalculatorProps> = ({ answers, onAnswer }) => {
  const [height, setHeight] = useState(answers.height?.toString() || '');
  const [weight, setWeight] = useState(answers.weight?.toString() || '');
  const [feet, setFeet] = useState('');
  const [inches, setInches] = useState('');
  const [calculatedBmi, setCalculatedBmi] = useState<number | null>(null);
  const [useImperial, setUseImperial] = useState(false);

  useEffect(() => {
    let totalHeight = 0;
    const w = parseFloat(weight);
    
    if (useImperial) {
      const ft = parseFloat(feet);
      const ins = parseFloat(inches);
      
      if (ft > 0 && ins >= 0 && ins <= 11 && w > 0) {
        // Convert feet + inches to total inches, then to cm
        const totalInches = (ft * 12) + ins;
        totalHeight = totalInches * 2.54; // Convert to cm
      }
    } else {
      const h = parseFloat(height);
      if (h > 0 && w > 0) {
        totalHeight = h; // Already in cm
      }
    }
    
    if (totalHeight > 0 && w > 0) {
      const weightKg = useImperial ? w * 0.453592 : w; // More precise conversion
      const bmi = weightKg / ((totalHeight / 100) * (totalHeight / 100));
      
      setCalculatedBmi(bmi);
      onAnswer('height', totalHeight);
      onAnswer('weight', weightKg);
      onAnswer('bmi', bmi);
    }
  }, [height, weight, feet, inches, useImperial, onAnswer]);

  const getBmiCategory = (bmi: number) => {
    if (bmi < 25) return 'LA31984-0'; // Lower than 25 kg/m^2
    if (bmi < 30) return 'LA31985-7'; // 25-30 kg/m^2
    return 'LA31986-5'; // Higher than 30 kg/m^2
  };

  const getBmiCategoryLabel = (bmi: number) => {
    if (bmi < 25) return 'Under 25 kg/m²';
    if (bmi < 30) return '25–30 kg/m²';
    return 'Over 30 kg/m²';
  };

  const currentBmi = calculatedBmi || 0;

  return (
    <div className="space-y-4">
      <Card className="bg-secondary/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            BMI Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-center mb-4">
            <Tabs value={useImperial ? "imperial" : "metric"} onValueChange={(value) => setUseImperial(value === "imperial")} className="w-fit">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="metric">Metric</TabsTrigger>
                <TabsTrigger value="imperial">Imperial</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {useImperial ? (
              <div className="space-y-2">
                <Label>Height (ft + in)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      type="number"
                      placeholder="5"
                      value={feet}
                      onChange={(e) => setFeet(e.target.value)}
                      min="3"
                      max="8"
                      step="1"
                    />
                    <Label className="text-xs text-muted-foreground mt-1">ft</Label>
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="9"
                      value={inches}
                      onChange={(e) => setInches(e.target.value)}
                      min="0"
                      max="11"
                      step="1"
                    />
                    <Label className="text-xs text-muted-foreground mt-1">in</Label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="height">Height (cm)</Label>
                <Input
                  id="height"
                  type="number"
                  placeholder="170"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  min="100"
                  max="250"
                  step="1"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="weight">
                Weight ({useImperial ? 'lbs' : 'kg'})
              </Label>
              <Input
                id="weight"
                type="number"
                placeholder={useImperial ? "154" : "70"}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                min={useImperial ? "66" : "30"}
                max={useImperial ? "660" : "300"}
                step={useImperial ? "0.1" : "1"}
              />
            </div>
          </div>
          
          {calculatedBmi && (
            <div className="p-3 bg-primary/10 rounded-lg">
              <p className="text-sm font-medium">
                Your BMI: <span className="text-primary font-bold">{calculatedBmi.toFixed(1)}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Category: {getBmiCategoryLabel(calculatedBmi)}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {currentBmi > 0 && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">BMI Category</Label>
          <RadioGroup
            value={getBmiCategory(currentBmi)}
            className="space-y-2"
          >
            <div className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${getBmiCategory(currentBmi) === 'LA31984-0' ? 'bg-secondary/30' : 'bg-secondary/20'}`}>
              <RadioGroupItem value="LA31984-0" id="LA31984-0" disabled />
              <Label htmlFor="LA31984-0" className="flex-1 text-sm">
                Under 25 kg/m²
              </Label>
            </div>
            <div className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${getBmiCategory(currentBmi) === 'LA31985-7' ? 'bg-secondary/30' : 'bg-secondary/20'}`}>
              <RadioGroupItem value="LA31985-7" id="LA31985-7" disabled />
              <Label htmlFor="LA31985-7" className="flex-1 text-sm">
                25–30 kg/m²
              </Label>
            </div>
            <div className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${getBmiCategory(currentBmi) === 'LA31986-5' ? 'bg-secondary/30' : 'bg-secondary/20'}`}>
              <RadioGroupItem value="LA31986-5" id="LA31986-5" disabled />
              <Label htmlFor="LA31986-5" className="flex-1 text-sm">
                Over 30 kg/m²
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}
    </div>
  );
};